import { wrapApiCall } from '@/lib/axios'
import type { AxiosInstance } from 'axios'
import {
  CreateEquipmentPayload,
  CreateInspectionPayload,
  CreatePMSchedulePayload,
  CreateVendorPayload,
  CreateWorkOrderPayload,
  EquipmentByStatusReport,
  EquipmentByTypeReport,
  EquipmentDetail,
  EquipmentListItem,
  EquipmentStats,
  InspectionListItem,
  PMScheduleListItem,
  TopMaintenanceReport,
  VendorListItem,
  WorkOrderListItem,
  WorkOrdersByMonthReport,
} from './model'

export interface PaginationParams {
  page: number
  pageSize: number
  query?: string
}

export interface PaginatedResponse<T> {
  data: T[]
  pagination: { total: number; page: number; pageSize: number }
}

export const EquipmentService = (client: AxiosInstance) => {
  return {
    async getEquipmentList(
      params: PaginationParams,
      type?: string,
      status?: string
    ): Promise<PaginatedResponse<EquipmentListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResponse<EquipmentListItem>>('/equipment', {
          params: { ...params, type, status },
        })
      )
      return data
    },

    async getEquipmentDetail(id: number | string): Promise<EquipmentDetail> {
      const { data } = await wrapApiCall(() => client.get<EquipmentDetail>(`/equipment/${id}`))
      return data
    },

    async createEquipment(payload: CreateEquipmentPayload): Promise<EquipmentListItem> {
      const { data } = await wrapApiCall(() =>
        client.post<EquipmentListItem>('/equipment', payload)
      )
      return data
    },

    async updateEquipment(
      id: number | string,
      payload: Partial<CreateEquipmentPayload>
    ): Promise<EquipmentListItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<EquipmentListItem>(`/equipment/${id}`, payload)
      )
      return data
    },

    async deleteEquipment(id: number | string): Promise<void> {
      await wrapApiCall(() => client.delete(`/equipment/${id}`))
    },

    async getEquipmentStats(): Promise<EquipmentStats> {
      const { data } = await wrapApiCall(() => client.get<EquipmentStats>('/equipment/stats'))
      return data
    },

    async getWorkOrders(
      params: PaginationParams,
      equipmentId?: number,
      status?: string,
      priority?: string
    ): Promise<PaginatedResponse<WorkOrderListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResponse<WorkOrderListItem>>('/equipment/work-orders', {
          params: { ...params, equipmentId, status, priority },
        })
      )
      return data
    },

    async createWorkOrder(payload: CreateWorkOrderPayload): Promise<WorkOrderListItem> {
      const { data } = await wrapApiCall(() =>
        client.post<WorkOrderListItem>('/equipment/work-orders', payload)
      )
      return data
    },

    async updateWorkOrder(
      id: number | string,
      payload: Partial<
        CreateWorkOrderPayload & { status: string; completedAt: string; cost: number }
      >
    ): Promise<WorkOrderListItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<WorkOrderListItem>(`/equipment/work-orders/${id}`, payload)
      )
      return data
    },

    async getInspections(
      params: PaginationParams,
      equipmentId?: number,
      date?: string
    ): Promise<PaginatedResponse<InspectionListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResponse<InspectionListItem>>('/equipment/inspections', {
          params: { ...params, equipmentId, date },
        })
      )
      return data
    },

    async createInspection(payload: CreateInspectionPayload): Promise<InspectionListItem> {
      const { photos, ...rest } = payload

      if (photos && photos.length > 0) {
        const formData = new FormData()
        formData.append('equipmentId', String(rest.equipmentId))
        formData.append('condition', rest.condition)
        if (rest.notes !== undefined) formData.append('notes', rest.notes)
        if (rest.hasDefect !== undefined) formData.append('hasDefect', String(rest.hasDefect))
        rest.checkedItems.forEach((item) => formData.append('checkedItems', item))
        photos.forEach((photo) => formData.append('photos', photo))

        const { data } = await wrapApiCall(() =>
          client.post<InspectionListItem>('/equipment/inspections', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          })
        )
        return data
      }

      const { data } = await wrapApiCall(() =>
        client.post<InspectionListItem>('/equipment/inspections', rest)
      )
      return data
    },

    async getPMSchedules(
      params: PaginationParams,
      equipmentId?: number,
      overdue?: boolean
    ): Promise<PaginatedResponse<PMScheduleListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResponse<PMScheduleListItem>>('/equipment/pm-schedules', {
          params: { ...params, equipmentId, overdue },
        })
      )
      return data
    },

    async createPMSchedule(payload: CreatePMSchedulePayload): Promise<PMScheduleListItem> {
      const { data } = await wrapApiCall(() =>
        client.post<PMScheduleListItem>('/equipment/pm-schedules', payload)
      )
      return data
    },

    async completePMSchedule(id: number | string, doneDate: string): Promise<PMScheduleListItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<PMScheduleListItem>(`/equipment/pm-schedules/${id}/complete`, { doneDate })
      )
      return data
    },

    async getVendors(
      params: PaginationParams,
      serviceType?: string
    ): Promise<PaginatedResponse<VendorListItem>> {
      const { data } = await wrapApiCall(() =>
        client.get<PaginatedResponse<VendorListItem>>('/equipment/vendors', {
          params: { ...params, serviceType },
        })
      )
      return data
    },

    async createVendor(payload: CreateVendorPayload): Promise<VendorListItem> {
      const { data } = await wrapApiCall(() =>
        client.post<VendorListItem>('/equipment/vendors', payload)
      )
      return data
    },

    async updateVendor(
      id: number | string,
      payload: Partial<CreateVendorPayload>
    ): Promise<VendorListItem> {
      const { data } = await wrapApiCall(() =>
        client.patch<VendorListItem>(`/equipment/vendors/${id}`, payload)
      )
      return data
    },

    async deleteVendor(id: number | string): Promise<void> {
      await wrapApiCall(() => client.delete(`/equipment/vendors/${id}`))
    },

    async getWorkOrdersByMonth(year?: number): Promise<WorkOrdersByMonthReport> {
      const { data } = await wrapApiCall(() =>
        client.get<WorkOrdersByMonthReport>('/equipment/reports/work-orders-by-month', {
          params: year ? { year } : {},
        })
      )
      return data
    },

    async getEquipmentByType(): Promise<EquipmentByTypeReport> {
      const { data } = await wrapApiCall(() =>
        client.get<EquipmentByTypeReport>('/equipment/reports/by-type')
      )
      return data
    },

    async getEquipmentByStatus(): Promise<EquipmentByStatusReport> {
      const { data } = await wrapApiCall(() =>
        client.get<EquipmentByStatusReport>('/equipment/reports/by-status')
      )
      return data
    },

    async getTopMaintenance(limit = 5): Promise<TopMaintenanceReport> {
      const { data } = await wrapApiCall(() =>
        client.get<TopMaintenanceReport>('/equipment/reports/top-maintenance', {
          params: { limit },
        })
      )
      return data
    },
  }
}
